/**
 * PushPermissionBanner — appears on EVERY login until the user has paired a
 * web push device on this account. Session-only dismiss (re-shows on next
 * login). Hidden permanently for a user once they actually enable.
 */
import { useCallback, useEffect, useState } from "react";
import { Bell, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SESSION_DISMISS_KEY = "webpush_banner_dismissed_this_session";

function urlB64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

async function pairThisBrowser() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("This browser does not support Web Push. Use Chrome / Edge / Firefox on desktop or Android, or Safari 16.4+ on iOS.");
    }
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
        throw new Error(`Permission ${perm}. Enable notifications for this site in your browser settings.`);
    }
    const reg = await navigator.serviceWorker.register("/service-worker.js");
    await navigator.serviceWorker.ready;
    const { data } = await api.get("/push/public-key");
    const vapid = data?.key;
    if (!vapid) throw new Error("Server hasn't generated a VAPID public key yet — try again in a moment.");
    let sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
    sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(vapid),
    });
    await api.post("/push/subscribe", { subscription: sub.toJSON() });
}


export default function PushPermissionBanner() {
    const { user } = useAuth();
    const [needed, setNeeded] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [enabling, setEnabling] = useState(false);

    const check = useCallback(async () => {
        if (!user || !user.id) { setNeeded(false); return; }
        // We deliberately do NOT short-circuit on Notification.permission === "denied"
        // because: (a) some browsers (including Playwright headless) report that
        // by default even though the user never denied, and (b) showing the banner
        // even when permission is denied gives the user a path to fix it — the
        // click handler shows the precise permission-state error.
        try {
            const { data } = await api.get("/tenant/push/devices");
            const hasWeb = (data?.devices || []).some(d => d.platform === "web");
            setNeeded(!hasWeb);
        } catch (_) {
            setNeeded(false);
        }
    }, [user]);

    useEffect(() => {
        // Session-only dismiss: cleared every fresh login (sessionStorage
        // dies with the tab so re-login on a new tab brings the banner back).
        try { if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") setDismissed(true); } catch (_) { /* sessionStorage blocked */ }
        check();
    }, [check]);

    const enable = async () => {
        setEnabling(true);
        try {
            await pairThisBrowser();
            toast.success("Notifications enabled — you'll receive alerts on this device.");
            setNeeded(false);
        } catch (e) {
            toast.error(e?.message || apiError(e), { duration: 10000 });
        } finally { setEnabling(false); }
    };

    const dismissForSession = () => {
        try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch (_) { /* sessionStorage blocked */ }
        setDismissed(true);
    };

    if (!needed || dismissed) return null;

    return (
        <div className="bg-amber-500/10 border-b border-amber-500/40 px-4 py-2.5"
            data-testid="webpush-enable-banner">
            <div className="max-w-7xl mx-auto flex items-center gap-3 flex-wrap">
                <Bell className="w-4 h-4 text-amber-400 flex-shrink-0" strokeWidth={1.5}/>
                <div className="flex-1 min-w-[200px] text-xs">
                    <span className="text-amber-400 font-medium">Enable browser notifications</span>
                    <span className="text-muted-foreground hidden sm:inline"> — get hearing reminders, cause-list updates and important alerts on this device.</span>
                </div>
                <button onClick={enable} disabled={enabling}
                    data-testid="webpush-banner-enable-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-amber-950 text-[10px] uppercase tracking-[0.16em] font-medium hover:bg-amber-400 disabled:opacity-50">
                    {enabling ? <Loader2 className="w-3 h-3 animate-spin"/> : <Bell className="w-3 h-3" strokeWidth={2}/>}
                    {enabling ? "Enabling…" : "Enable"}
                </button>
                <button onClick={dismissForSession}
                    title="Hide for this session (banner will return on next login)"
                    aria-label="Hide for this session"
                    data-testid="webpush-banner-dismiss"
                    className="p-1.5 text-muted-foreground hover:text-foreground">
                    <X className="w-3.5 h-3.5"/>
                </button>
            </div>
        </div>
    );
}
